/*
    ZDROJ PRAVDY pro stránku Dokumenty ke stažení.

    PRAVIDLO 1: `key` je zároveň i18n klíč ČESKÉHO názvu v namespace modules/document.

    PRAVIDLO 2: ANGLICKÉ názvy jsou DATA SADY, ne překlad UI. Musí zůstat anglicky
      i pro českého uživatele, tedy se NIKDY nepřekládají — proto leží tady a NESMÍ
      jít do public/locales/**. Locale aplikace je vždy 'cs': bere se z cookie `lang`
      (utils/helpers/server/serverSideProps/ssrProps.ts:91-92), kterou nastavuje jen
      setLangCookies() volané z LangMenu, a LangMenu je v AppLayout.tsx:168 vyřazený
      dead-kódem `&& false`. Soubor public/locales/en/modules/document.json se tedy
      nikdy nenačte a anglické názvy by se z něj nevykreslily.

    PRAVIDLO 3: Pořadí v poli = pořadí na stránce. Nikdy neřaď za běhu — locale-sensitive
      řazení na serveru (node:14-alpine) vs. v prohlížeči je zdroj hydration mismatche
      a tiše přeuspořádá produkční stránku, aniž by to bylo vidět v diffu.

    PRAVIDLO 4: Přepínač přepíná SADU DOKUMENTŮ, NIKOLI jazyk aplikace. Rám aplikace
      (menu, titulek, popisky) zůstává vždy český.
 */

export enum DocSetEnum {
    cs = 'cs',
    en = 'en',
}

export type DocSection = 'requestsAndForms' | 'guidesAndManuals' | 'various';

export const DOC_SECTIONS: readonly DocSection[] = [
    'requestsAndForms',
    'guidesAndManuals',
    'various',
];

/** Query parametr, kterým se sada drží v URL. */
export const DOC_SET_QUERY_KEY = 'set';

export const FACR_WEB_HOST = 'https://facr.fotbal.cz';

/** Názvy sad — každý ve svém jazyce, záměrně nepřekládané (viz PRAVIDLO 2). */
export const DOC_SET_LABELS: Record<DocSetEnum, string> = {
    [DocSetEnum.cs]: 'Dokumenty v češtině',
    [DocSetEnum.en]: 'Documents in English',
};

/*
    Nadpisy sekcí anglické sady = data sady (viz PRAVIDLO 2). Struktura sekcí je
    v obou sadách stejná; sekce bez jediné položky se nevykreslí.
 */
export const EN_SECTION_LABELS: Record<DocSection, string> = {
    requestsAndForms: 'Requests and Forms',
    guidesAndManuals: 'Guides and Manuals',
    various: 'Miscellaneous',
};

interface CsVariant {
    /** ID souboru v CMS facr.fotbal.cz; vzájemně výlučné s internalUrl */
    docId?: number;
    /** interní routa IS2 (dnes jediný případ: generalTerms) */
    internalUrl?: string;
    /** default true; u interní routy se předává false */
    newWindow?: boolean;
}

interface EnVariant {
    docId: number;
    /** Zobrazovaný anglický název. Povinný — nelze přidat ID bez názvu. */
    title: string;
    /*
        ID české revize, ze které anglická verze vznikla. Povinné všude, kde položka
        má i `cs`. Hlídá test: jakmile se změní cs.docId (což se reálně děje —
        commit 5a8d623 z 2026-07-09 vyměnil přestupní lístek 158435 → 171100),
        test spadne a autor musí buď dodat novou anglickou verzi, nebo vědomě
        označit enOutdated. Bez toho by zahraniční klub tiše dostal neplatnou revizi.
     */
    basedOnCsDocId?: number;
    /** Vědomé povolení rozjeté revize. Odznak „EN verze“ se pak nerenderuje. */
    enOutdated?: boolean;
}

export interface DocumentDef {
    /** stabilní technické id položky = i18n klíč českého názvu */
    key: string;
    section: DocSection;
    /** chybí jen u dokumentů, které existují pouze anglicky */
    cs?: CsVariant;
    /** chybí u dokumentů bez anglické verze (dnes 32 z 35) */
    en?: EnVariant;
}

export const csHref = (doc: DocumentDef): string =>
    doc.cs.internalUrl ?? `${FACR_WEB_HOST}/document/download/${doc.cs.docId}`;

export const enHref = (doc: DocumentDef): string =>
    `${FACR_WEB_HOST}/document/download/${doc.en.docId}`;

/** Odznak „EN verze“ u české položky se nabízí jen u aktuální anglické revize. */
export const hasFreshEn = (doc: DocumentDef): boolean =>
    !!doc.en && !doc.en.enOutdated && doc.en.basedOnCsDocId === doc.cs?.docId;

export const DOCUMENTS: readonly DocumentDef[] = [
    // ── Žádosti a formuláře (19 CZ) ───────────────────────────────
    {
        key: 'freeTransferCancellation',
        section: 'requestsAndForms',
        cs: { docId: 158452 },
    },
    {
        key: 'ofsTransferConfirmation',
        section: 'requestsAndForms',
        cs: { docId: 162517 },
    },
    {
        key: 'studyConfirmation',
        section: 'requestsAndForms',
        cs: { docId: 158441 },
    },
    {
        key: 'noUseByHomeClub',
        section: 'requestsAndForms',
        cs: { docId: 158432 },
    },
    /*
        ENG 171332 „Transfer ticket_Přestupní lístek_verze 1. 7. 2026_ENG“
        Párování jisté: název souboru je dvojjazyčný, obsahuje „Přestupní lístek“
        i verzi 1. 7. 2026, což je přesně revize 171100 (commit 5a8d623).
     */
    {
        key: 'transferTicket',
        section: 'requestsAndForms',
        cs: { docId: 171100 },
        en: {
            docId: 171332,
            title: 'Transfer Ticket',
            basedOnCsDocId: 171100,
        },
    },
    {
        key: 'otherLegalPersonRegistration',
        section: 'requestsAndForms',
        cs: { docId: 158453 },
    },
    {
        key: 'clubRegistration',
        section: 'requestsAndForms',
        cs: { docId: 158454 },
    },
    /*
        ENG 171333 „Player Consent to Transfer“ — doslovný překlad „Souhlas hráče
        s přestupem“. V CZ sadě je to jediný dokument typu „souhlas hráče“;
        158442 je souhlas KLUBU s uvolněním hráče do zahraničí.
     */
    {
        key: 'playerAgreement',
        section: 'requestsAndForms',
        cs: { docId: 158434 },
        en: {
            docId: 171333,
            title: 'Player Consent to Transfer',
            basedOnCsDocId: 158434,
        },
    },
    {
        key: 'homePlayerToForeign',
        section: 'requestsAndForms',
        cs: { docId: 158442 },
    },
    /*
        ENG 171334 „Application for International Player Transfer“ — párování
        potvrzeno Janem Hýskem (přes Jana Sochora, 2026-07-31): je to anglická
        verze „Vyžádání hráče ze zahraničí“.
     */
    {
        key: 'foreignPlayer',
        section: 'requestsAndForms',
        cs: { docId: 158433 },
        en: {
            docId: 171334,
            title: 'Application for International Player Transfer',
            basedOnCsDocId: 158433,
        },
    },
    {
        key: 'membershipClubChange',
        section: 'requestsAndForms',
        cs: { docId: 161318 },
    },
    {
        key: 'clubNameChange',
        section: 'requestsAndForms',
        cs: { docId: 162472 },
    },
    {
        key: 'playerRegistrationArchiving',
        section: 'requestsAndForms',
        cs: { docId: 160462 },
    },
    {
        key: 'administratorRegistration',
        section: 'requestsAndForms',
        cs: { docId: 158437 },
    },
    {
        key: 'mainAdminClubRegistration',
        section: 'requestsAndForms',
        cs: { docId: 158440 },
    },
    {
        key: 'foreignYoungRegistration',
        section: 'requestsAndForms',
        cs: { docId: 158436 },
    },
    {
        key: 'cancelMemberRegistration',
        section: 'requestsAndForms',
        cs: { docId: 158439 },
    },
    {
        key: 'cancelClubRegistration',
        section: 'requestsAndForms',
        cs: { docId: 158438 },
    },
    {
        key: 'cancelAdminRegistration',
        section: 'requestsAndForms',
        cs: { docId: 161317 },
    },

    // ── Návody a příručky (9 CZ) ──────────────────────────────────
    { key: 'faq', section: 'guidesAndManuals', cs: { docId: 158455 } },
    {
        key: 'notifications',
        section: 'guidesAndManuals',
        cs: { docId: 158465 },
    },
    {
        key: 'amateurPlayerForeignRequest',
        section: 'guidesAndManuals',
        cs: { docId: 161945 },
    },
    { key: 'nordVpnGuide', section: 'guidesAndManuals', cs: { docId: 162471 } },
    { key: 'userManual', section: 'guidesAndManuals', cs: { docId: 158458 } },
    {
        key: 'userManualFinance',
        section: 'guidesAndManuals',
        cs: { docId: 158475 },
    },
    {
        key: 'userManualPlayers',
        section: 'guidesAndManuals',
        cs: { docId: 158459 },
    },
    {
        key: 'userManualClubCard',
        section: 'guidesAndManuals',
        cs: { docId: 167817 },
    },
    { key: 'matchDate', section: 'guidesAndManuals', cs: { docId: 158460 } },

    // ── Různé (7 CZ) ──────────────────────────────────────────────
    { key: 'playerList', section: 'various', cs: { docId: 158461 } },
    { key: 'onlineTransfer', section: 'various', cs: { docId: 158462 } },
    {
        key: 'onlinePlayerRegistration',
        section: 'various',
        cs: { docId: 158463 },
    },
    {
        key: 'foreignYoungPlayerManual',
        section: 'various',
        cs: { docId: 158456 },
    },
    {
        key: 'viceAdministratorRegistration',
        section: 'various',
        cs: { docId: 158457 },
    },
    { key: 'photoAdd', section: 'various', cs: { docId: 158464 } },
    {
        key: 'generalTerms',
        section: 'various',
        cs: {
            internalUrl: '/clenstvi-podminky?discipline=football',
            newWindow: false,
        },
    },
];

/*
    PŘIDÁNÍ 4. ANGLICKÉHO DOKUMENTU = JEDEN BLOK U EXISTUJÍCÍ POLOŽKY:

      { key: 'studyConfirmation', section: 'requestsAndForms', cs: { docId: 158441 },
        en: { docId: 171501, title: 'Confirmation of Study', basedOnCsDocId: 158441 } },

    Nic jiného se nemění: ani JSON s překlady, ani komponenta, ani config. Položka se
    objeví v ENG sadě pod svou sekcí, počet na pilulce se přepočítá na (4)
    a v CZ sadě se u „Potvrzení o studiu“ objeví odznak „EN verze“.
 */

export const CS_DOCUMENTS = DOCUMENTS.filter((doc) => doc.cs);
export const EN_DOCUMENTS = DOCUMENTS.filter((doc) => doc.en);

/** Anglická sada zatím nepokrývá celou českou — řídí odznaky „EN verze“ v CZ sadě. */
export const IS_PARTIAL = EN_DOCUMENTS.length < CS_DOCUMENTS.length;
